import axios from "axios";

// Use environment variable for backend URL, default to localhost for local development
const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const getAuthHeaders = () => {
  const token = localStorage.getItem("admin-token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function fetchProducts() {
  try {
    const res = await axios.get(`${API_URL}/api/dashboard-admin/products`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error(
      "Error fetching products:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function saveProduct(formData, productId) {
  try {
    const headers = {
      ...getAuthHeaders(),
      "Content-Type": "multipart/form-data",
    };

    let res;
    if (productId) {
      res = await axios.put(
        `${API_URL}/api/dashboard-admin/products/${productId}`,
        formData,
        {
          headers,
        },
      );
    } else {
      res = await axios.post(
        `${API_URL}/api/dashboard-admin/products`,
        formData,
        {
          headers,
        },
      );
    }

    return res.data;
  } catch (err) {
    console.error(
      "❌ Error saving product:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function fetchFlaggedReviews({ limit = 20, offset = 0 } = {}) {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      flaggedOnly: "true",
    });

    const res = await axios.get(
      `${API_URL}/api/dashboard-admin/reviews?${params}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error fetching flagged reviews:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function approveReview(reviewId) {
  try {
    const res = await axios.patch(
      `${API_URL}/api/dashboard-admin/reviews/${reviewId}/approve`,
      {},
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error("Error approving review:", err.response?.data || err.message);
    throw err;
  }
}

export async function rejectReview(reviewId, reason = "Inappropriate content") {
  try {
    const res = await axios.patch(
      `${API_URL}/api/dashboard-admin/reviews/${reviewId}/reject`,
      { reason },
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error("Error rejecting review:", err.response?.data || err.message);
    throw err;
  }
}

export async function deleteProduct(id) {
  try {
    const res = await axios.delete(
      `${API_URL}/dashboard-admin/products/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error("Error deleting product:", err.response?.data || err.message);
    throw err;
  }
}

export async function updateProductPlacements(productId, pagePlacements) {
  try {
    const res = await axios.patch(
      `${API_URL}/dashboard-admin/products/${productId}/page-placements`,
      { pagePlacements },
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error updating product placements:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function bulkUpdateProducts(productIds, updates) {
  try {
    const res = await axios.patch(
      `${API_URL}/dashboard-admin/products/bulk-update`,
      { productIds, updates },
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error bulk updating products:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function bulkDeleteProducts(productIds) {
  try {
    const res = await axios.delete(
      `${API_URL}/dashboard-admin/products/bulk-delete`,
      {
        data: { productIds },
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error bulk deleting products:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function exportProductsCsv() {
  try {
    const res = await axios.get(
      `${API_URL}/dashboard-admin/products/export/csv`,
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error exporting products CSV:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

export async function importProductsCsv(csvText) {
  try {
    const res = await axios.post(
      `${API_URL}/dashboard-admin/products/import/csv`,
      { csvText },
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error importing products CSV:",
      err.response?.data || err.message,
    );
    throw err;
  }
}

// The following section uses fetch and different URL constants for dashboard stats.
// I kept the original structure for consistency with the code you provided.

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export async function fetchDashboardData() {
  console.log("--- DEBUG: Frontend API Call Started ---");
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/dashboard/stats`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    // Check 1: Log the response status
    console.log(
      `DEBUG: Response Status: ${response.status} (${response.statusText})`,
    );

    if (response.status === 401 || response.status === 403) {
      console.error("!!! ERROR: Authentication Failed. Check token and roles.");
      throw new Error("Unauthorized access");
    }

    if (!response.ok) {
      // Try to read the error message from the backend
      const errorData = await response.json();
      console.error("!!! ERROR: Backend returned error:", errorData);
      throw new Error(errorData.message || "Failed to fetch dashboard data.");
    }

    const data = await response.json();

    // Check 2: Log the raw data received from the backend
    console.log("DEBUG: Raw Data Received:", data);

    // Check 3: Log the parsed data before returning it to the component
    const parsedData = {
      totalProducts: Number(data.totalProducts),
      totalOrders: Number(data.totalOrders),
      pendingOrders: Number(data.pendingOrders),
      totalSales: parseFloat(data.totalSales || 0),
      newCustomers: Number(data.newCustomers),
    };

    console.log("DEBUG: Parsed Data Sent to Component:", parsedData);
    console.log("--- DEBUG: Frontend API Call Finished Successfully ---");

    return parsedData;
  } catch (error) {
    console.error("!!! ERROR: Failed to fetch dashboard data:", error);
    throw error;
  }
}

export async function fetchRecentOrders({ limit = 10, from, to, status } = {}) {
  console.log("--- DEBUG: fetchRecentOrders started ---");
  try {
    const params = new URLSearchParams();
    if (limit != null) params.append("limit", String(limit));
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (status) params.append("status", status);

    const url =
      params.toString().length > 0
        ? `${API_BASE_URL}/api/dashboard-admin/orders?${params.toString()}`
        : `${API_BASE_URL}/api/dashboard-admin/orders`;

    console.log("DEBUG: Fetching recent orders URL:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    console.log(
      `DEBUG: Response Status: ${response.status} (${response.statusText})`,
    );

    if (response.status === 401 || response.status === 403) {
      console.error(
        "!!! ERROR: Authentication Failed while fetching recent orders.",
      );
      return [];
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("!!! ERROR: Backend returned error for orders:", errorData);
      return [];
    }

    const data = await response.json();
    console.log("DEBUG: Raw orders data:", data);

    // normalize to an array: backend may return { orders: [...] } or [...]
    const orders = Array.isArray(data) ? data : data.orders || [];

    console.log(
      `DEBUG: Returning ${orders.length} recent order(s) from fetchRecentOrders`,
    );
    // When used in Orders.jsx for pagination, the full data object with 'total' is required.
    // The previous implementation for dashboard was returning only the orders array.
    // Let's modify this function slightly to return the full data object if it contains 'total'
    // but default to an array structure for simplicity in the dashboard view.
    return Array.isArray(data) ? orders : data;
  } catch (error) {
    console.error("!!! ERROR: fetchRecentOrders failed:", error);
    return [];
  }
}

// ----------------------------------------------------
// NEW FUNCTIONS ADDED TO FIX THE Dashboard.jsx ERRORS
// ----------------------------------------------------

/**
 * Fetches the top-selling products for the dashboard view.
 * Assumes backend endpoint: /api/dashboard-admin/dashboard/top-products
 */
export async function fetchTopProducts({ limit = 5 } = {}) {
  console.log("--- DEBUG: fetchTopProducts started ---");
  try {
    const params = new URLSearchParams();
    if (limit != null) params.append("limit", String(limit));

    const url = `${API_BASE_URL}/api/dashboard-admin/products/top?${params.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "!!! ERROR: Backend returned error for top products:",
        errorData,
      );
      return [];
    }
    const data = await response.json();
    console.log("DEBUG: Returning top products data:", data);
    // Assuming backend returns an array of products or an object with a 'products' array
    return Array.isArray(data) ? data : data.products || [];
  } catch (error) {
    console.error("!!! ERROR: fetchTopProducts failed:", error);
    return [];
  }
}

/**
 * Fetches products that are running low on stock.
 * Assumes backend endpoint: /api/dashboard-admin/dashboard/low-stock
 */
export async function fetchLowStockProducts({ limit = 5, threshold = 5 } = {}) {
  console.log("--- DEBUG: fetchLowStockProducts started ---");
  try {
    const params = new URLSearchParams();
    if (limit != null) params.append("limit", String(limit));
    if (threshold != null) params.append("threshold", String(threshold));

    const url = `${API_BASE_URL}/api/dashboard-admin/products/low-stock?${params.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "!!! ERROR: Backend returned error for low stock:",
        errorData,
      );
      return [];
    }
    const data = await response.json();
    console.log("DEBUG: Returning low stock data:", data);
    // Assuming backend returns an array of products or an object with a 'products' array
    return Array.isArray(data) ? data : data.products || [];
  } catch (error) {
    console.error("!!! ERROR: fetchLowStockProducts failed:", error);
    return [];
  }
}

/**
 * Fetches recently registered customers for the dashboard.
 * Assumes backend endpoint: /api/dashboard-admin/dashboard/recent-customers
 */
export async function fetchRecentCustomers({ limit = 5 } = {}) {
  console.log("--- DEBUG: fetchRecentCustomers started ---");
  try {
    const params = new URLSearchParams();
    if (limit != null) params.append("limit", String(limit));

    const url = `${API_BASE_URL}/api/dashboard-admin/customers/recent?${params.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "!!! ERROR: Backend returned error for recent customers:",
        errorData,
      );
      return [];
    }
    const data = await response.json();
    console.log("DEBUG: Returning recent customers data:", data);
    // Assuming backend returns an array of customers or an object with a 'customers' array
    return Array.isArray(data) ? data : data.customers || [];
  } catch (error) {
    console.error("!!! ERROR: fetchRecentCustomers failed:", error);
    return [];
  }
}

// ----------------------------------------------------
// ✅ NEW FUNCTION: Update Order Status
// ----------------------------------------------------

/**
 * Updates the status of a specific order.
 * Assumes backend endpoint: /api/dashboard-admin/orders/:orderId/status
 * @param {string} orderId - The ID of the order to update.
 * @param {object} updates - An object containing the new status, e.g., { status: 'shipped' }.
 */
export async function updateOrderStatus(orderId, updates) {
  console.log(
    `--- DEBUG: updateOrderStatus started for ID: ${orderId}, status: ${updates.status} ---`,
  );
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/orders/${orderId}/status`,
      {
        method: "PATCH", // PATCH is commonly used for partial updates
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      },
    );

    console.log(
      `DEBUG: Status update response status: ${response.status} (${response.statusText})`,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("!!! ERROR: Failed to update order status:", errorData);
      throw new Error(errorData.message || "Failed to update order status.");
    }

    const data = await response.json();
    console.log("DEBUG: Status update successful. Response data:", data);

    // Return the updated order object from the backend
    return data;
  } catch (error) {
    console.error("!!! ERROR: updateOrderStatus failed:", error);
    throw error;
  }
}

/**
 * ADMIN: Fetch full order details with customer info and items
 * Used by OrderDetails page to display complete order information
 * @param {string|number} orderId - The ID of the order to fetch
 * @returns {object} Order object with customer, items, and address details
 */
export async function getOrderDetailsAdmin(orderId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/orders/${orderId}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to fetch order details");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching order details:", error);
    throw error;
  }
}

/**
 * Fetches the order data as a CSV blob for download.
 * Assumes backend endpoint: /api/dashboard-admin/orders/export
 */
export async function exportOrdersCsv({ from, to, status } = {}) {
  console.log("--- DEBUG: exportOrdersCsv started ---");
  try {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (status) params.append("status", status);

    const url =
      params.toString().length > 0
        ? `${API_BASE_URL}/api/dashboard-admin/orders/export/csv?${params.toString()}`
        : `${API_BASE_URL}/api/dashboard-admin/orders/export/csv`;

    console.log("DEBUG: Export CSV URL:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // The backend likely returns JSON error data
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "!!! ERROR: Backend returned error for CSV export:",
        errorData,
      );
      throw new Error(errorData.message || "Failed to export orders.");
    }

    // Crucial: The backend must respond with 'Content-Type: text/csv'
    // We return the response as a Blob object for the client (Orders.jsx) to download
    return response.blob();
  } catch (error) {
    console.error("!!! ERROR: exportOrdersCsv failed:", error);
    throw error;
  }
}

export async function fetchAdminCategories() {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/categories`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to fetch categories.");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to fetch admin categories:", error);
    throw error;
  }
}

export async function createAdminCategory(payload) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/categories`,
      {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to create category.");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to create admin category:", error);
    throw error;
  }
}

export async function updateAdminCategory(categoryId, payload) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/categories/${categoryId}`,
      {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to update category.");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to update admin category:", error);
    throw error;
  }
}

export async function deleteAdminCategory(categoryId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/dashboard-admin/categories/${categoryId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to delete category.");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to delete admin category:", error);
    throw error;
  }
}

// ============================================
// NEW REVIEW MANAGEMENT FUNCTIONS
// ============================================

export async function fetchRecentReviews({
  limit = 10,
  offset = 0,
  productId,
} = {}) {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (productId) params.append("productId", productId);

    const res = await axios.get(
      `${API_URL}/api/dashboard-admin/reviews?${params}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error("Error fetching reviews:", err.response?.data || err.message);
    throw err;
  }
}

export async function deleteAdminReview(reviewId) {
  try {
    const res = await axios.delete(
      `${API_URL}/api/dashboard-admin/reviews/${reviewId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (err) {
    console.error("Error deleting review:", err.response?.data || err.message);
    throw err;
  }
}
